import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiV1 } from "../Auth/Interceptor";

const initialStateValue = {
    categories: {
        categories: [],
        loading: 'idle',
        error: null,
    },
    categoryCollections: {
        categoryCollections: [],
        loading: 'idle',
        error: null,
    },
    collectionData: {
        collectionData: {},
        loading: 'idle',
        error: null,
    },
    customCollectionData: {
        customCollectionData: {},
        loading: 'idle',
        error: null,
    },
    customCollections: {
        customCollections: [],
        loading: 'idle',
        error: null,
    },
    categoryKebab: {
        kebabOpen: false,
        categoryId: null,
    },
    collectionKebab: {
        kebabOpen: false,
        collectionId: null,
    },
    customCollectionKebab: {
        kebabOpen: false,
        customCollectionId: null,
    },
    modal: {
        activeModal: '',
        displayModal: false,
    },
    activeSection: 'categories',
    selectedKebabItem: {},
}

const fetchCategories = createAsyncThunk('collections/fetchCategories', async () => {
    try {
        const response = await apiV1.get(`category/getAll`);
        return response.data.data.category;
    }
    catch(error) {
        throw error;
    }
});

const fetchCustomCollections = createAsyncThunk('collections/fetchCustomCollections', async () => {
    try {
        const response = await apiV1.get(`customCollection/getAll`);
        return response.data.data.collection;
    }
    catch(error) {
        throw error;
    }
});

const fetchCategoryCollections = createAsyncThunk('collections/fetchCategoryCollections', async (categoryId) => {
    try {
        const response = await apiV1.get(`collection/getAll/${categoryId}`);
        return response.data.data.collection;
    }
    catch(error) {
        throw error;
    }
});

const fetchCollectionData = createAsyncThunk('collections/fetchCollectionData', async (collectionId) => {
    try {
        const response = await apiV1.get(`collection/${collectionId}`);
        return response.data.data;
    }
    catch(error) {
        throw error;
    }
});

const fetchCustomCollectionData = createAsyncThunk('collections/fetchCustomCollectionData', async (customCollectionId) => {
    try {
        const response = await apiV1.get(`customCollection/${customCollectionId}`);
        return response.data.data;
    }
    catch(error) {
        throw error;
    }
});

const collectionsSlice = createSlice({
    name: 'collections',
    initialState: initialStateValue,
    reducers: {
        setCollectionData: (state, action) => {
            state.collectionData.collectionData = action.payload;
        },
        setCustomCollectionData: (state, action) => {
            state.customCollectionData.customCollectionData = action.payload;
        },
        openCategoryKebabMenu: (state, action) => {
            state.categoryKebab.kebabOpen = true;
            state.categoryKebab.categoryId = action.payload;
        },
        closeCategoryKebabMenu: (state, _) => {
            state.categoryKebab = initialStateValue.categoryKebab;
        },
        openCollectionKebabMenu: (state, action) => {
            state.collectionKebab.kebabOpen = true;
            state.collectionKebab.collectionId = action.payload;
        },
        closeCollectionKebabMenu: (state, _) => {
            state.collectionKebab = initialStateValue.collectionKebab;
        },
        openCustomCollectionKebabMenu: (state, action) => {
            state.customCollectionKebab.kebabOpen = true;
            state.customCollectionKebab.customCollectionId = action.payload;
        },
        closeCustomCollectionKebabMenu: (state, _) => {
            state.customCollectionKebab = initialStateValue.customCollectionKebab;
        },
        setModal: (state, action) => {
            state.modal.activeModal = action.payload;
            state.modal.displayModal = true;
        },
        closeModal: (state, _) => {
            state.modal.activeModal = '';
            state.modal.displayModal = false;
        },
        setActiveSection: (state, action) => {
            state.activeSection = action.payload;
        },
        setSelectedKebabItem: (state, action) => {
            state.selectedKebabItem = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchCategories.pending, (state) => {
            state.categories.loading = 'loading';
        })
        .addCase(fetchCategories.fulfilled, (state, action) => {
            state.categories.loading = 'succeeded';
            state.categories.categories = action.payload;
        })
        .addCase(fetchCategories.rejected, (state, action) => {
            state.categories.loading = 'failed';
            state.categories.error = action.error.message;
        })
        .addCase(fetchCustomCollections.pending, (state) => {
            state.customCollections.loading = 'loading';
        })
        .addCase(fetchCustomCollections.fulfilled, (state, action) => {
            state.customCollections.loading = 'succeeded';
            state.customCollections.customCollections = action.payload;
        })
        .addCase(fetchCustomCollections.rejected, (state, action) => {
            state.customCollections.loading = 'failed';
            state.customCollections.error = action.error.message;
        })
        .addCase(fetchCategoryCollections.pending, (state) => {
            state.categoryCollections.loading = 'loading';
        })
        .addCase(fetchCategoryCollections.fulfilled, (state, action) => {
            state.categoryCollections.loading = 'succeeded';
            state.categoryCollections.categoryCollections = action.payload;
            state.categoryCollections.error = null;
        })
        .addCase(fetchCategoryCollections.rejected, (state, action) => {
            state.categoryCollections.loading = 'failed';
            state.categoryCollections.error = action.error.message;
        })
        .addCase(fetchCollectionData.pending, (state) => {
            state.collectionData.loading = 'loading';
        })
        .addCase(fetchCollectionData.fulfilled, (state, action) => {
            state.collectionData.loading = 'succeeded';
            state.collectionData.collectionData = action.payload;
            state.collectionData.error = null;
        })
        .addCase(fetchCollectionData.rejected, (state, action) => {
            state.collectionData.loading = 'failed';
            state.collectionData.error = action.error.message;
        })
        .addCase(fetchCustomCollectionData.pending, (state) => {
            state.customCollectionData.loading = 'loading';
        })
        .addCase(fetchCustomCollectionData.fulfilled, (state, action) => {
            state.customCollectionData.loading = 'succeeded';
            state.customCollectionData.customCollectionData = action.payload;
            state.customCollectionData.error = null;
        })
        .addCase(fetchCustomCollectionData.rejected, (state, action) => {
            state.customCollectionData.loading = 'failed';
            state.customCollectionData.error = action.error.message;
        });
    },
});

export const {
    setCollectionData,
    openCategoryKebabMenu,
    closeCategoryKebabMenu,
    openCollectionKebabMenu,
    closeCollectionKebabMenu,
    setModal,
    closeModal,
    setActiveSection,
    openCustomCollectionKebabMenu,
    closeCustomCollectionKebabMenu,
    setCustomCollectionData,
    setSelectedKebabItem,
} = collectionsSlice.actions;

export {
    fetchCategories,
    fetchCustomCollections,
    fetchCategoryCollections,
    fetchCollectionData,
    fetchCustomCollectionData,
}

export default collectionsSlice.reducer;
